import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
  useReactTable,
  Row,
  RowModel,
  Table,
} from '@tanstack/react-table';
import useResize from 'hooks/useResize';
import {
  useState, useEffect, useRef, useContext, useMemo,
} from 'react';
import {
  getTaxReportsTableData,
  getLaminasTableData,
  TableBaseApiKind,
  SortDescription,
  TableDataRes,
  SortDirection,
} from 'components/table/base/tableBaseApi';
import TableFooter from 'components/table/base/tableFooter';
import TableSortIcon from 'assets/icons/tableSort.svg';
import Spinner from 'components/spinner';
import { useTranslation } from 'react-i18next';
import { StoreContext } from 'models/import';

export const getDefaultEmptyValue = (): null | JSX.Element => <span>---</span>;

export interface TableBaseProps<T, U> {
  columns: ColumnDef<T>[];
  requestUri: string;
  modifier?: TableModifier;

  pageSize?: number;
  pageIndex?: number;

  hideMobileTable?: boolean;
  hideHeader?: boolean;
  mockData?: boolean;

  columnVisibility?: Record<string, boolean>;
  initialSorting?: SortingState;
  filters?: string;
  requiresFiltersForFirstLoad: boolean;
  apiKind: TableBaseApiKind;
  loadingError?: React.ReactElement;

  onError?: (e: Error) => void;
  onDataLoaded?: (res: TableDataRes<T, U>) => void;

  getRowCanExpand?: ((row: Row<T>) => boolean) | undefined;
  getIsRowExpanded?: ((row: Row<T>) => boolean) | undefined;
  getExpandedRowModel?: ((table: Table<any>) => () => RowModel<any>) | undefined;
  renderSubComponent?: (props: { row: Row<T> }) => React.ReactElement;
}

export enum TableModifier {
  IMPORT_TABLE = 'importTable',
  IMPORT_TABLELEFT = 'importTableLeft',
  API_TABLE = 'apiTable',
  BALANCE_TABLE = 'balanceTable',
  CSV_PREVIEW_TABLE = 'csvPreviewTable',
  VALIDATION_TABLE = 'validationTable',
}

// Extend T with every TableDef, when creating a new wrapper table
function TableBase<T, U>(props: TableBaseProps<T, U>) {
  const { windowWidth } = useResize();
  const [tableData, setTableData] = useState<T[]>([]);
  const data = useMemo(() => tableData, [tableData]);
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [sorting, setSorting] = useState<SortingState>(props.initialSorting || []);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [totalItemsCount, setTotalItemsCount] = useState<number | undefined>(
    undefined,
  );

  const [cacheDateTime, setCacheDateTime] = useState(null);
  const [needsRefreshingCachedData, setNeedsRefreshingCachedData] = useState(false);
  const [needsReloadParam, setNeedsReloadParam] = useState(0);

  const { t } = useTranslation();
  const controller = useRef<AbortController>(new AbortController());
  const { userSettings } = useContext(StoreContext);

  const getTableData = (
    apiKind: TableBaseApiKind,
    page: number,
    limit: number,
    sort: SortDescription | undefined,
    filters: string,
    signal: AbortSignal,
  ): Promise<TableDataRes<T, U>> => {
    switch (apiKind) {
      case TableBaseApiKind.TAX_REPORTS:
        return getTaxReportsTableData<T, U>(
          props.requestUri,
          page,
          limit,
          sort,
          filters,
          userSettings.userId,
          signal,
          props.mockData,
        );
      default:
        // TODO: Hackish way to force a reload.
        // A better solution would be to have the network call lifted outside the framework
        let filterBefore: string = filters;
        if ((needsRefreshingCachedData)) {
          if (filterBefore.includes('&_=')) {
            filterBefore = filterBefore.replace(/&_=(\d+)+/g, `&_=${needsReloadParam}`);
          } else {
            filterBefore += `&_=${needsReloadParam}`;
          }
          if (filterBefore.includes('&filter[forceRefreshCache]=0')) {
            filterBefore = filterBefore.replace('&filter[forceRefreshCache]=0', '&filter[forceRefreshCache]=1');
          }
          setNeedsRefreshingCachedData(false);
        }
        return getLaminasTableData<T, U>(
          props.requestUri,
          page,
          limit,
          sort,
          filterBefore,
          signal,
          props.mockData,
        );
    }
  };

  const table = useReactTable({
    data,
    columns: props.columns,
    manualPagination: true,
    pageCount: totalPageCount,
    initialState: {
      pagination: {
        pageSize: props.pageSize || 10,
        pageIndex: props.pageIndex || 0,
      },
      columnVisibility: props.columnVisibility,
    },
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: true,
    enableSortingRemoval: false,
    getRowCanExpand: props.getRowCanExpand,
    getIsRowExpanded: props.getIsRowExpanded,
    getExpandedRowModel: props.getExpandedRowModel,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    const { pageIndex, pageSize } = table.getState().pagination;
    const filters = props.filters || '';
    const tempPageIndex = props.mockData ? pageIndex + 1 : pageIndex;
    const sort = sorting.map((s) => (
      {
        sortingField: s.id,
        sortingDirection: s.desc ? SortDirection.DESC : SortDirection.ASC,
      }
    ))[0];

    setIsLoading(true);
    if (isFirstLoad && props.requiresFiltersForFirstLoad && filters === '') return;

    // if (controller.current) {
    //   controller.current.abort();
    // }
    controller.current = new AbortController();
    const { signal } = controller.current;

    getTableData(props.apiKind, tempPageIndex, pageSize, sort, filters, signal)
      .then((res: TableDataRes<T, U>) => {
        const {
          meta, data, info, cacheDateTime,
        } = res;

        if (data && meta) {
          const tItemsCount = meta.total_item_count || 0;
          const tPagesCount = Math.ceil(tItemsCount / pageSize);

          setTotalItemsCount(tItemsCount);
          setTotalPageCount(tPagesCount);
          setCacheDateTime(cacheDateTime);
          setTableData(data);
          setIsLoading(false);
          setIsFirstLoad(false);

          if (props.onDataLoaded) props.onDataLoaded(res);
        }
      })
      .catch((e: Error) => {
        // emit error to parent
        if (props.onError) props.onError(e);
        setIsLoading(false);
        setIsError(true);
      });

    return () => {
      // controller.current.abort();
    };
  }, [
    table.getState().pagination.pageIndex,
    table.getState().pagination.pageSize,
    sorting,
    props.filters,
    needsReloadParam,
  ]);

  useEffect(() => {
    const md = 768;
    setIsMobile(windowWidth < md);
  }, [windowWidth]);

  const noResult = () => (
    <tr>
      <td colSpan={table.getHeaderGroups()[0].headers.length}>
        <div className="ct_tableBase-noResult">
          {!props.filters && props.loadingError && (
            props.loadingError
          )}
          {!!props.filters && (
            <span>{t(isError ? 'Some error occurred.' : 'No matches found for the given filter criteria.')}</span>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className={`ct_table ${isLoading ? '_isLoading' : ''}`}>
      {isLoading && (<Spinner />) }
      {/* Desktop Table Layout */}
      {!!tableData && isMobile !== null && (!isMobile || props.hideMobileTable) && (
        <div className={`ct_tableBase ${props.modifier ? `_${props.modifier}` : ''}`}>
          <table className="ct_tableBase-table">
            {!props.hideHeader && (
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <div
                            aria-hidden="true"
                            {...{
                              className: `ct_tableBase-sort ${
                                header.column.getCanSort()
                                  ? '_pointer _noSelect'
                                  : ''
                              }`,
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {!!header.column.getIsSorted()
                              && header.column.getIsSorted() === 'asc' && (
                                <i className="ct_tableBase-sortIcon _asc">
                                  <TableSortIcon />
                                </i>
                            )}
                            {!!header.column.getIsSorted()
                              && header.column.getIsSorted() === 'desc' && (
                                <i className="ct_tableBase-sortIcon _desc">
                                  <TableSortIcon />
                                </i>
                            )}
                            {header.column.getCanSort()
                              && !header.column.getIsSorted() && (
                                <i className="ct_tableBase-sortIcon">
                                  <TableSortIcon />
                                </i>
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            )}
            <tbody>
              {!!tableData.length && table.getRowModel().rows.map((row) => (
                <>
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                  {props.renderSubComponent && row.getIsExpanded() && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length}>
                      <div className="ct_tableBase-embeddedTable">
                        {props.renderSubComponent({ row })}
                      </div>
                    </td>
                  </tr>
                  )}
                </>
              ))}
              {!isLoading && !tableData.length && noResult()}
            </tbody>
          </table>
        </div>
      )}
      {/* Mobile Table Layout */}
      {!props.hideMobileTable && !!tableData && isMobile !== null && isMobile && (
        <div className="ct_tableBase">
            {!!tableData.length && table.getRowModel().rows.map((row) => (
              <>
                <div className="ct_tableBase-tbody" key={row.id}>
                  <table className="ct_tableBase-table">
                    <tbody key={row.id}>
                      {row.getVisibleCells().map((cell, index) => (
                        <tr key={cell.id}>
                          {table.getHeaderGroups().map((headerGroup) => {
                            const header = headerGroup.headers[index];

                            return (header.column.columnDef.header
                                && (
                                  <td key={cell.id}>
                                    <div className="ct_tableBase-mobileHeader" key={headerGroup.id}>
                                      {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                          header.column.columnDef.header,
                                          header.getContext(),
                                        )}
                                    </div>
                                  </td>
                                )
                            );
                          })}
                          <td>
                            <div key={row.index}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {props.renderSubComponent && row.getIsExpanded() && (
                      <tr>
                        <td colSpan={2}>
                          <div className="ct_tableBase-embeddedTableMobile">
                            {props.renderSubComponent({ row })}
                          </div>
                        </td>
                      </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="ct_tableBase-embeddedTableMobileSpacing" />
              </>
            ))}
            {!isLoading && !tableData.length && (
              <table className="ct_tableBase-table">
                <tbody>{noResult()}</tbody>
              </table>
            )}
        </div>
      )}
      <TableFooter
        isBottom
        pageIndex={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        cacheDateTime={cacheDateTime}
        totalPageCount={totalPageCount}
        totalItemsCount={totalItemsCount}
        siblingCount={1}
        onPaginate={(v) => table.setPageIndex(v)}
        onPageSizeSelect={(v: number) => table.setPageSize(v)}
        refreshCacheAction={() => {
          setNeedsRefreshingCachedData(true);
          let param = Math.floor(Math.random() * 10);
          while (needsReloadParam === param) {
            param = Math.floor(Math.random() * 10);
          }
          setNeedsReloadParam(param);
        }}
        isLoading={isLoading}
        modifiers={[]}
      />
    </div>
  );
}

TableBase.defaultProps = { mockData: false, ...{ } };

export default TableBase;
